import { AllKeyOf } from 'chaika-common-types';

import {
    DiagnosisType,
    EntryStatus,
    EntryTypeType,
    FhirTaskStatus,
    LaboratoryServiceOption,
    LaboratoryTestStatus,
    LegalEntityStatus,
    Locale,
    OrderStatus,
    OrganizationStatus,
    PaymentMethodType,
    ReferralStatus,
    RenderedServiceEventName,
    RenderedServiceStatus,
    RepresentativeType,
    ReservationStatus,
    ReservationType,
    ServiceCategory,
    TaskStatus,
    ProtestActItemStatus,
    PlanType,
    PayerType,
    FinancePaymentStatus,
    OperationType,
    LaboratoryServiceServiceStatus,
    EntryStatusCustom,
    ProtestActStatus,
    SubscriptionOrderStatus,
    MedicalCaseStatus,
    DrugStatus,
    FieldStatus,
    PriceStatus,
    RecurringSubscriptionStatus,
    RecurringSubscriptionRenewPeriod,
    SubscriptionPriceStatus,
    RoleName,
    SubscriptionStatus,
    ContractStatus,
    Channel,
    LegalEntityType,
    OrganizationType,
    ConditionsAgreement,
    Languages,
    GrantSource,
} from 'chaika-enums';

import { LegalEntityBranchStatus } from '@organization/store/legalEntityBranch/types';

import { translate } from '@chaika/i18n/helper';
import { DiscountType, MedicationStatus, Sex } from 'chaika-enums/src/enums';

function getIfExist(dic: Record<string, string>, key: string): string {
    return dic[key] ?? key;
}

export function getLanguagesTranslation(key: Locale): string {
    const StatusDic: AllKeyOf<Locale> = {
        [Locale.Ru]: translate('russian'),
        [Locale.En]: translate('english'),
        [Locale.Am]: translate('armenian'),
        [Locale.Ge]: translate('georgian'),
        [Locale.Pt]: translate('portuguese'),
    };

    return getIfExist(StatusDic, key);
}

export function getLanguageTranslation(key: Languages): string {
    const LanguageDic: AllKeyOf<Languages> = {
        [Languages.English]: translate('english'),
        [Languages.Arab]: translate('arab'),
        [Languages.Chinese]: translate('chinese'),
        [Languages.German]: translate('german'),
        [Languages.French]: translate('french'),
        [Languages.Spanish]: translate('spanish'),
        [Languages.Portuguese]: translate('portuguese'),
        [Languages.Turkish]: translate('turkish'),
        [Languages.Russian]: translate('russian'),
        [Languages.Georgian]: translate('georgian'),
        [Languages.Azerbaijani]: translate('azerbaijani'),
        [Languages.Armenian]: translate('armenian'),
        [Languages.Belorussian]: translate('belorussian'),
        [Languages.Kazakh]: translate('kazakh'),
        [Languages.Kyrgyz]: translate('kyrgyz'),
        [Languages.Moldavian]: translate('moldavian'),
        [Languages.Tajik]: translate('tajik'),
        [Languages.Turkmen]: translate('turkmen'),
        [Languages.Uzbek]: translate('uzbek'),
        [Languages.Ukrainian]: translate('ukrainian'),
        [Languages.Hebrew]: translate('hebrew'),
        [Languages.Dutch]: translate('dutch'),
    };

    return getIfExist(LanguageDic, key);
}

export function getCommonStatusTranslation(status: 'active' | 'draft' | 'archived' | 'archive' | 'planned'): string {
    const StatusDic = {
        active: translate('activeMasculine'),
        draft: translate('draft'),
        archived: translate('archive'),
        archive: translate('archive'),
        archive1: translate('planned'),
    };

    return getIfExist(StatusDic, status);
}

export function getCommonStatusFemeTranslation(
    status: 'active' | 'draft' | 'archived' | 'archive' | 'planned',
): string {
    const StatusDic = {
        active: translate('activeFeminine'),
        draft: translate('draft'),
        archived: translate('archiveFem'),
        archive: translate('archiveFem'),
        archive1: translate('plannedFemenine'),
        planned: translate('plannedFemenine'),
    };

    return getIfExist(StatusDic, status);
}

export function getRepresentativeTypeTranslation(key: RepresentativeType): string {
    const StatusDic: AllKeyOf<RepresentativeType> = {
        [RepresentativeType.Child]: translate('child'),
        [RepresentativeType.Trustee]: translate('trustee'),
        [RepresentativeType.Other]: translate('other'),
    };

    return getIfExist(StatusDic, key);
}

export function getRenderedServiceStatusTranslation(status: RenderedServiceStatus): string {
    const StatusDic: AllKeyOf<RenderedServiceStatus> = {
        [RenderedServiceStatus.Unpaid]: translate('unpaid'),
        [RenderedServiceStatus.Paid]: translate('paid'),
        [RenderedServiceStatus.Canceled]: translate('canceled'),
        [RenderedServiceStatus.Deleted]: translate('canceled'),
        [RenderedServiceStatus.Refunded]: translate('refunded'),
        [RenderedServiceStatus.Credited]: translate('credited'),
        [RenderedServiceStatus.PaymentPending]: translate('paymentPending'),
    };

    return getIfExist(StatusDic, status);
}

export function getReservationStatusTranslation(status: ReservationStatus): string {
    const StatusDic: AllKeyOf<ReservationStatus> = {
        [ReservationStatus.Reserved]: translate('activeFemSecond'),
        [ReservationStatus.Cancelled]: translate('canceledFem'),
        [ReservationStatus.VisitOccurred]: translate('completedFem'),
    };

    return getIfExist(StatusDic, status);
}

export function getReservationTypeTranslation(key: ReservationType): string {
    const StatusDic: AllKeyOf<ReservationType> = {
        [ReservationType.Visit]: translate('visit'),
        [ReservationType.OnlineVisit]: translate('onlineVisit'),
        [ReservationType.Draft]: translate('draft'),
        [ReservationType.Lunch]: translate('lunch'),
        [ReservationType.Vacation]: translate('vacation'),
        [ReservationType.Disease]: translate('diseaseSchedule'),
        [ReservationType.Meeting]: translate('meeting'),
        [ReservationType.Training]: translate('training'),
        [ReservationType.Vaccination]: translate('vaccinationSingle'),
        [ReservationType.Collection]: translate('fiveMinutes'),
        [ReservationType.NotWorkTime]: translate('notWorkTime'),
        [ReservationType.WaitingList]: translate('waitingList'),
        [ReservationType.Operation]: translate('operation'),
        [ReservationType.Appointment]: translate('appointment'),
        [ReservationType.OnlineAppointment]: translate('onlineAppointment'),
        [ReservationType.TimeBlock]: translate('timeBlock'),
        [ReservationType.RoomBooking]: translate('roomBooking'),
    };

    return getIfExist(StatusDic, key);
}

export function getReferralStatusTranslation(status: ReferralStatus): string {
    const StatusDic: AllKeyOf<ReferralStatus> = {
        [ReferralStatus.Active]: translate('activeNeuter'),
        [ReferralStatus.Cancelled]: translate('canceled'),
        [ReferralStatus.Executed]: translate('performed'),
        [ReferralStatus.Expired]: translate('expired'),
    };

    return getIfExist(StatusDic, status);
}

export function getLegalEntityStatusTranslation(status: LegalEntityStatus): string {
    const StatusDic: AllKeyOf<LegalEntityStatus> = {
        [LegalEntityStatus.Active]: translate('activeNeuter'),
        [LegalEntityStatus.Archived]: translate('archive'),
    };

    return getIfExist(StatusDic, status);
}

export function getLegalEntityBranchStatusTranslation(status: LegalEntityBranchStatus): string {
    const StatusDic: AllKeyOf<LegalEntityBranchStatus> = {
        [LegalEntityBranchStatus.Active]: translate('activeFemSecond'),
        [LegalEntityBranchStatus.Archive]: translate('archive'),
        [LegalEntityBranchStatus.Planned]: translate('planneFemSecond'),
    };

    return getIfExist(StatusDic, status);
}

export function getOrganizationStatusTranslation(status: OrganizationStatus): string {
    const StatusDic: AllKeyOf<OrganizationStatus> = {
        [OrganizationStatus.Active]: translate('activeNeuter'),
        [OrganizationStatus.Archived]: translate('archive'),
    };

    return getIfExist(StatusDic, status);
}

export function getEntryTypeTypeTranslation(status: EntryTypeType): string {
    const StatusDic: AllKeyOf<EntryTypeType> = {
        [EntryTypeType.Consultation]: translate('consultation'),
        [EntryTypeType.UsDiagnostics]: translate('usDiagnostics'),
        [EntryTypeType.Diagnostics]: translate('diagnostics'),
        [EntryTypeType.Manipulation]: translate('manipulation'),
        [EntryTypeType.Tests]: translate('analyzes'),
        [EntryTypeType.System]: translate('systemFem'),
    };

    return getIfExist(StatusDic, status);
}

export function getLocaleTranslation(locale: Locale): string {
    const StatusDic: AllKeyOf<Locale> = {
        [Locale.Ru]: translate('russian'),
        [Locale.En]: translate('english'),
        [Locale.Ge]: translate('georgian'),
        [Locale.Am]: translate('armenian'),
        [Locale.Pt]: translate('portuguese'),
    };

    return getIfExist(StatusDic, locale);
}

export function getPaymentMethodTypeTranslation(value: PaymentMethodType, isForAdmin?: boolean): string {
    const StatusDic: AllKeyOf<PaymentMethodType> = {
        [PaymentMethodType.RaiffeisenSbpLink]: translate('sbpLink'),
        [PaymentMethodType.PaymentUrlTypeSBP]: translate('sbpLink'),
        [PaymentMethodType.RaiffeisenTerminal]: translate('raiffeisenTerminal'),
        [PaymentMethodType.MerchantAcquiring]: translate('terminal'),
        [PaymentMethodType.RaiffeisenQrPlate]: translate('plate'),
        [PaymentMethodType.InternetAcquiring]: translate('linkedCard'),
        [PaymentMethodType.PaymentUrlTypeYooKassa]: 'YooKassa',
        [PaymentMethodType.Manual]: translate('manually'),
        [PaymentMethodType.ManualWithoutReceipt]: translate('manuallyWithoutReceipt'),
        [PaymentMethodType.IDramLight]: 'iDram',
        [PaymentMethodType.TelcellLight]: 'Telcell',
        [PaymentMethodType.ArcaLink]: translate('arcaLink'),
        [PaymentMethodType.Transfer]: translate('byDetails'),
        [PaymentMethodType.Cash]: translate('cash'),
        [PaymentMethodType.PersonalAccount]: translate('personalAccount'),
        [PaymentMethodType.Yookassa]: 'YooKassa',
        [PaymentMethodType.SetOffRefund]: 'SetOffRefund',
        [PaymentMethodType.MockTerminal]: isForAdmin ? translate('mockTerminal') : translate('terminal'),
        [PaymentMethodType.MockCash]: isForAdmin ? translate('mockCash') : translate('cash'),
        [PaymentMethodType.YookassaCard]: translate('card'),
        [PaymentMethodType.TBankLinkSplit]: translate('tbankSplit'),
    };

    return getIfExist(StatusDic, value);
}

export function getOperationTypeTranslation(value: OperationType): string {
    const StatusDic: AllKeyOf<OperationType> = {
        [OperationType.Payment]: translate('payment'),
        [OperationType.Refund]: translate('refund'),
        [OperationType.SubscriptionRefund]: translate('refund'),
        [OperationType.AccountReplenishment]: translate('account_replenishment'),
        [OperationType.RefundToAccount]: translate('refund_to_account'),
        [OperationType.WriteOff]: translate('write_off'),
        [OperationType.Subscription]: translate('subscription'),
    };

    return getIfExist(StatusDic, value);
}

export function getServiceCategoryTranslation(value: ServiceCategory): string {
    const StatusDic: AllKeyOf<ServiceCategory> = {
        [ServiceCategory.Consultation]: translate('consultation'),
        [ServiceCategory.Dentistry]: translate('dentistry'),
        [ServiceCategory.Laboratory]: translate('laboratoryWitch'),
        [ServiceCategory.Archive]: translate('archive'),
        [ServiceCategory.Technical]: translate('technical'),
        [ServiceCategory.Clinic]: translate('policlinika'),
        [ServiceCategory.Radiodiagnosis]: translate('radiodiagnosis'),
        [ServiceCategory.UltrasoundDiagnostics]: translate('ultrasoundDiagnostics'),
        [ServiceCategory.CheckUp]: translate('checkup'),
        [ServiceCategory.Hospital]: translate('hospital'),
        [ServiceCategory.MaterialsAndPreparations]: translate('materialsAndPreparations'),
        [ServiceCategory.OneCProduct]: translate('oneCProduct'),
        [ServiceCategory.Vaccination]: translate('vaccination'),
    };

    return getIfExist(StatusDic, value);
}

export function getEntryStatusTranslation(value: EntryStatus): string {
    const StatusDic: AllKeyOf<EntryStatus> = {
        [EntryStatus.Draft]: translate('draft'),
        [EntryStatus.Signed]: translate('published'),
        [EntryStatus.Archive]: translate('archive'),
        [EntryStatus.Unsigned]: translate('unsigned'),
        [EntryStatus.ReadyForDescribing]: translate('readyForDescribing'),
        [EntryStatus.OnReview]: translate('onReview'),
        [EntryStatus.ReviewCompleted]: translate('reviewCompleted'),
    };

    return getIfExist(StatusDic, value);
}

export function getCustomEntryStatusTranslation(value: EntryStatusCustom): string {
    const StatusDic: AllKeyOf<EntryStatusCustom> = {
        [EntryStatusCustom.WithEds]: translate('signedEDS'),
        [EntryStatusCustom.WithoutEds]: translate('unsignedSecond'),
    };

    return getIfExist(StatusDic, value);
}

export function getOrderStatusTranslation(value: OrderStatus): string {
    const StatusDic: AllKeyOf<OrderStatus> = {
        [OrderStatus.Canceled]: translate('canceled'),
        [OrderStatus.Draft]: translate('draft'),
        [OrderStatus.Executed]: translate('performed'),
    };

    return getIfExist(StatusDic, value);
}

export function getLaboratoryTestStatusTranslation(value: LaboratoryTestStatus): string {
    const StatusDic: AllKeyOf<LaboratoryTestStatus> = {
        [LaboratoryTestStatus.InProgress]: translate('inProgress'),
        [LaboratoryTestStatus.Canceled]: translate('canceled'),
        [LaboratoryTestStatus.Done]: translate('performed'),
        [LaboratoryTestStatus.Failed]: translate('error'),
    };

    return getIfExist(StatusDic, value);
}

export function getFhirTaskStatusTranslation(value: FhirTaskStatus): string {
    const StatusDic: AllKeyOf<FhirTaskStatus> = {
        [FhirTaskStatus.Draft]: translate('draft'),
        [FhirTaskStatus.Requested]: translate(FhirTaskStatus.Requested),
        [FhirTaskStatus.Received]: translate(FhirTaskStatus.Received),
        [FhirTaskStatus.Accepted]: translate('accepted'),
        [FhirTaskStatus.Rejected]: translate('rejected'),
        [FhirTaskStatus.Ready]: translate('ready'),
        [FhirTaskStatus.Cancelled]: translate('canceled'),
        [FhirTaskStatus.InProgress]: translate('inProgress'),
        [FhirTaskStatus.OnHold]: translate('onHold'),
        [FhirTaskStatus.Failed]: translate('error'),
        [FhirTaskStatus.Completed]: translate('completed'),
        [FhirTaskStatus.EnteredInError]: translate('enteredInError'),
        // Deviation from FHIR
        [FhirTaskStatus.Started]: translate('started'),
        [FhirTaskStatus.Expired]: translate('overdue'),
    };

    return getIfExist(StatusDic, value);
}

export function getDiagnosisTypeTranslation(value: DiagnosisType): string {
    const StatusDic: AllKeyOf<DiagnosisType> = {
        [DiagnosisType.Chronic]: translate('chronic'),
        [DiagnosisType.Acute]: translate('acute'),
        [DiagnosisType.Preventative]: translate('preventative'),
    };

    return getIfExist(StatusDic, value);
}

export function getLaboratoryServiceOptionTranslation(value: LaboratoryServiceOption): string {
    const StatusDic: AllKeyOf<LaboratoryServiceOption> = {
        [LaboratoryServiceOption.Diuresis]: translate('diuresis'),
        [LaboratoryServiceOption.Height]: translate('height'),
        [LaboratoryServiceOption.Weight]: translate('weight'),
        [LaboratoryServiceOption.Medication]: translate('medicines'),
        [LaboratoryServiceOption.Menstruation]: translate('menstruation'),
        [LaboratoryServiceOption.Pregnancy]: translate('pregnancy'),
        [LaboratoryServiceOption.Localization]: translate('locationText'),
        [LaboratoryServiceOption.NumberFragments]: translate('countFragmentText'),
        [LaboratoryServiceOption.CharacterPathologicalProcess]: translate('pathologicalText'),
        [LaboratoryServiceOption.Comment]: translate('marking'),
        [LaboratoryServiceOption.CollectionTime]: translate('pickUpTime'),
    };

    return getIfExist(StatusDic, value);
}

export function getTaskStatusTranslation(value: TaskStatus): string {
    const StatusDic: AllKeyOf<TaskStatus> = {
        [TaskStatus.Requested]: translate('newNeutral'),
        [TaskStatus.Cancelled]: translate('canceled'),
        [TaskStatus.Completed]: translate('completed'),
    };

    return getIfExist(StatusDic, value);
}

export function getRenderedServiceEventNameTranslation(value: RenderedServiceEventName): string {
    const StatusDic: AllKeyOf<RenderedServiceEventName> = {
        [RenderedServiceEventName.RenderedServiceCanceled]: translate('canceledFem'),
        [RenderedServiceEventName.RenderedServiceCreated]: translate('createdFem'),
        [RenderedServiceEventName.RenderedServiceDeleted]: translate('deletedFem'),
        [RenderedServiceEventName.RenderedServiceMedicalCaseAdded]: translate('diagnosisAdded'),
        [RenderedServiceEventName.RenderedServiceMedicalCaseRemoved]: translate('diagnosisRemoved'),
        [RenderedServiceEventName.RenderedServicePaymentChanged]: translate('paymentAmountChange'),
        [RenderedServiceEventName.RenderedServicePlanAdded]: translate('programAdded'),
        [RenderedServiceEventName.RenderedServicePlanChanged]: translate('programChanged'),
        [RenderedServiceEventName.RenderedServicePlanRemoved]: translate('programRemoved'),
        [RenderedServiceEventName.RenderedServiceStatusChanged]: translate('changeOfStatus'),
        [RenderedServiceEventName.RenderedServiceUpdated]: translate('changedFem'),
        [RenderedServiceEventName.RenderedServiceContractorUpdated]: translate('contractorChanged'),
    };

    return getIfExist(StatusDic, value);
}

export function getPlanTypeTranslation(value: PlanType): string {
    const StatusDic: AllKeyOf<PlanType> = {
        [PlanType.Insurance]: translate('insurance'),
        [PlanType.GuarantyLetter]: translate('GPFull'),
        [PlanType.Organization]: translate('organization'),
        [PlanType.Doctor]: translate('doctor'),
        [PlanType.Patient]: translate('patient'),
        [PlanType.WaitForGuarantyLetter]: translate('waitingGP'),
        [PlanType.GuarantyLetterPlus]: translate('GPPlus'),
        [PlanType.Checkup]: translate('checkup'),
        [PlanType.DoctorInsurance]: 'A Plan',
        [PlanType.DoctorSubscription]: 'Doctor subscription',
    };

    return getIfExist(StatusDic, value);
}

export function getPayerTypeTranslation(value: PayerType): string {
    const StatusDic: AllKeyOf<PayerType> = {
        [PayerType.Individual]: translate('individual'),
        [PayerType.Legal]: translate('legalEntityShort'),
    };

    return getIfExist(StatusDic, value);
}

export function getFinancePaymentStatusTranslation(value: FinancePaymentStatus): string {
    const StatusDic: AllKeyOf<FinancePaymentStatus> = {
        [FinancePaymentStatus.Initialization]: translate('initialization'),
        [FinancePaymentStatus.InitializationFailed]: translate('initializationFailed'),
        [FinancePaymentStatus.Processing]: translate('processing'),
        [FinancePaymentStatus.ProcessingFailed]: translate('processingFailed'),
        [FinancePaymentStatus.WaitingForReceipt]: translate('waitingForReceipt'),
        [FinancePaymentStatus.ReceiptFailed]: translate('receiptFailed'),
        [FinancePaymentStatus.Completed]: translate('completedMasculine'),
        [FinancePaymentStatus.Canceled]: translate('canceledMale'),
    };

    return getIfExist(StatusDic, value);
}

export function getLaboratoryServiceServiceStatus(value: LaboratoryServiceServiceStatus): string {
    const StatusDic: AllKeyOf<LaboratoryServiceServiceStatus> = {
        [LaboratoryServiceServiceStatus.Active]: translate('activeFeminine'),
        [LaboratoryServiceServiceStatus.Archived]: translate('archiveFem'),
    };

    return getIfExist(StatusDic, value);
}

export function getProtestActStatusTranslation(value: ProtestActStatus): string {
    const StatusDic: AllKeyOf<ProtestActStatus> = {
        [ProtestActStatus.New]: translate('newNeutral'),
        [ProtestActStatus.InProcess]: translate('inProgress'),
        [ProtestActStatus.Finish]: translate('completed'),
        [ProtestActStatus.Deleted]: translate('deleted'),
    };

    return getIfExist(StatusDic, value);
}

export function getProtestItemStatusTranslation(value: ProtestActItemStatus): string {
    const StatusDic: AllKeyOf<ProtestActItemStatus> = {
        [ProtestActItemStatus.New]: translate('newNeutral'),
        [ProtestActItemStatus.InProcess]: translate('inProgress'),
        [ProtestActItemStatus.Processed]: translate('processed'),
        [ProtestActItemStatus.Finish]: translate('completed'),
    };

    return getIfExist(StatusDic, value);
}

export function getSubscriptionOrderStatusTranslation(value: SubscriptionOrderStatus): string {
    const StatusDic: AllKeyOf<SubscriptionOrderStatus> = {
        [SubscriptionOrderStatus.Active]: translate('activeFeminine'),
        [SubscriptionOrderStatus.Created]: translate('createdFem'),
        [SubscriptionOrderStatus.Canceled]: translate('canceledFem'),
        [SubscriptionOrderStatus.Paid]: translate('paidFem'),
        [SubscriptionOrderStatus.Expired]: translate('expiredFem'),
    };

    return getIfExist(StatusDic, value);
}

export function getRecurringSubscriptionStatusTranslation(value: RecurringSubscriptionStatus): string {
    const StatusDic: AllKeyOf<RecurringSubscriptionStatus> = {
        [RecurringSubscriptionStatus.Active]: translate('activeFeminine'),
        [RecurringSubscriptionStatus.Cancelled]: translate('expired'),
        [RecurringSubscriptionStatus.WaitingForPayment]: translate('waitingForPayment'),
    };

    return getIfExist(StatusDic, value);
}

export function getRecurringSubscriptionRenewPeriodTranslation(value: RecurringSubscriptionRenewPeriod): string {
    const StatusDic: AllKeyOf<RecurringSubscriptionRenewPeriod> = {
        [RecurringSubscriptionRenewPeriod.Monthly]: translate('monthlyRenewPeriodFem'),
        [RecurringSubscriptionRenewPeriod.Annual]: translate('annualRenewPeriodFem'),
    };

    return getIfExist(StatusDic, value);
}

export function getMedicalCaseStatusTranslation(value: MedicalCaseStatus): string {
    const StatusDic: AllKeyOf<MedicalCaseStatus> = {
        [MedicalCaseStatus.Open]: translate('activeMasculine'),
        [MedicalCaseStatus.Closed]: translate('closedMasculine'),
        [MedicalCaseStatus.OnHold]: translate('onHold'),
    };

    return getIfExist(StatusDic, value);
}

export function getDrugStatusTranslation(value: DrugStatus): string {
    const StatusDic: AllKeyOf<DrugStatus> = {
        [DrugStatus.Active]: translate('activeMasculine'),
        [DrugStatus.Cancelled]: translate('canceledMale'),
        [DrugStatus.CancelledAppointment]: translate('canceledMale'),
        [DrugStatus.CanceledByPatient]: translate('canceledMale'),
    };

    return getIfExist(StatusDic, value);
}

export function getDrugMedicationStatusCommonTranslation(value: DrugStatus | MedicationStatus): string {
    const DrugStatusDic: AllKeyOf<DrugStatus> = {
        [DrugStatus.Active]: translate('active'),
        [DrugStatus.Cancelled]: translate('canceled'),
        [DrugStatus.CancelledAppointment]: translate('canceled'),
        [DrugStatus.CanceledByPatient]: translate('canceled'),
    };
    const MedicationStatusDic: AllKeyOf<MedicationStatus> = {
        [MedicationStatus.Active]: translate('active'),
        [MedicationStatus.Deleted]: translate('canceled'),
        [MedicationStatus.CancelledAppointment]: translate('canceled'),
        [MedicationStatus.CanceledByPatient]: translate('canceled'),
    };
    const StatusDic = {
        ...DrugStatusDic,
        ...MedicationStatusDic,
    };

    return getIfExist(StatusDic, value);
}

export function getFieldStatusTranslation(value: FieldStatus): string {
    const StatusDic: AllKeyOf<FieldStatus> = {
        [FieldStatus.Active]: translate('activeMasculine'),
        [FieldStatus.Deleted]: translate('deleted'),
    };

    return getIfExist(StatusDic, value);
}

export function getPriceStatusTranslation(value: PriceStatus): string {
    const StatusDic: AllKeyOf<PriceStatus> = {
        [PriceStatus.Active]: translate('activeFeminine'),
        [PriceStatus.Archived]: translate('archive'),
        [PriceStatus.Planned]: translate('plannedFemenine'),
    };

    return getIfExist(StatusDic, value);
}

export function getSexTranslation(value: Sex): string {
    const StatusDic: AllKeyOf<Sex> = {
        [Sex.Male]: translate('male'),
        [Sex.Female]: translate('female'),
    };

    return getIfExist(StatusDic, value);
}

export function getSubscriptionUserPriceStatusTranslation(value: SubscriptionPriceStatus): string {
    const StatusDic: AllKeyOf<SubscriptionPriceStatus> = {
        [SubscriptionPriceStatus.Active]: translate('activeFeminine'),
        [SubscriptionPriceStatus.Archived]: translate('archive'),
    };

    return getIfExist(StatusDic, value);
}

export function getDiscountTypeTranslation(value: DiscountType): string {
    const StatusDic: AllKeyOf<DiscountType> = {
        [DiscountType.Average]: translate('discountTypeAverage'),
        [DiscountType.Flat]: translate('discountTypeFlat'),
        [DiscountType.Hurdle]: translate('discountTypeHurdle'),
    };

    return getIfExist(StatusDic, value);
}

export function getRoleTranslation(value: RoleName): string {
    const StatusDic: AllKeyOf<RoleName> = {
        [RoleName.Doctor]: translate('doctor'),
        [RoleName.Assistant]: translate('assistant'),
        [RoleName.HeadOfClinic]: translate('headOfClinic'),
        [RoleName.PracticeLeader]: translate('practiceLeader'),
        [RoleName.Helpdesk]: translate('helpdesk'),
        [RoleName.Developer]: translate('developer'),
        [RoleName.Accountant]: translate('accountant'),
        [RoleName.MedicalDirector]: translate('medicalDirector'),
        [RoleName.Care]: translate('care'),
        [RoleName.ProcedureNurse]: translate('procedureNurse'),
    };

    return getIfExist(StatusDic, value);
}

export function getSubscriptionStatus(value: SubscriptionStatus): string {
    const StatusDic: AllKeyOf<SubscriptionStatus> = {
        [SubscriptionStatus.Active]: translate('activeFeminine'),
        [SubscriptionStatus.Archived]: translate('archive'),
        [SubscriptionStatus.Planned]: translate('plannedFemenine'),
    };

    return getIfExist(StatusDic, value);
}

export function getContractStatus(value: ContractStatus): string {
    const StatusDic: AllKeyOf<ContractStatus> = {
        [ContractStatus.Active]: translate('active'),
        [ContractStatus.Archive]: translate('archive'),
    };

    return getIfExist(StatusDic, value);
}

export function getChannelTranslations(value: Channel): string {
    const StatusDic: AllKeyOf<Channel> = {
        [Channel.Sms]: 'sms',
        [Channel.Email]: 'email',
        [Channel.TgNotificationTopic]: 'NotificationTopic',
        [Channel.TgNotificationTopicStrict]: 'NotificationTopic Strict',
        [Channel.ChaikaTgBot]: 'ChaikaTgBot',
        [Channel.TelegramName]: 'TelegramName',
        [Channel.Viber]: 'viber',
        [Channel.FaceBook]: 'FaceBook',
        [Channel.Skype]: 'Skype',
        [Channel.WhatsApp]: 'WhatsApp',
        [Channel.Zoom]: 'Zoom',
        [Channel.ContactEmail]: 'ContactEmail',
        [Channel.CityPhone]: 'CityPhone',
        [Channel.UserTg]: 'UserTg',
        [Channel.UserPracticeTopic]: 'UserPracticeTopic',
        [Channel.PracticeTopic]: 'PracticeTopic',
        [Channel.Default]: 'Default',
    };

    return getIfExist(StatusDic, value);
}

export function getLegalEntityTypeTranslation(value: LegalEntityType): string {
    const StatusDic: AllKeyOf<LegalEntityType> = {
        [LegalEntityType.InsuranceCompany]: translate('insuranceCompany'),
        [LegalEntityType.Charity]: translate('charity'),
        [LegalEntityType.Assistance]: translate('assistance'),
        [LegalEntityType.Organization]: translate('organization'),
        [LegalEntityType.Clinic]: translate('clinic'),
        [LegalEntityType.ExternalClinic]: translate('externalClinic'),
        [LegalEntityType.Laboratory]: translate('laboratory'),
        [LegalEntityType.Contractor]: translate('contractor'),
    };

    return getIfExist(StatusDic, value);
}

export function getOrganizationTypeTranslation(value: OrganizationType): string {
    const StatusDic: AllKeyOf<OrganizationType> = {
        [OrganizationType.InsuranceCompany]: translate('insuranceCompany'),
        [OrganizationType.Charity]: translate('charity'),
        [OrganizationType.Assistance]: translate('assistance'),
        [OrganizationType.Organization]: translate('organization'),
        [OrganizationType.Clinic]: translate('clinic'),
        [OrganizationType.ExternalClinic]: translate('externalClinic'),
        [OrganizationType.Laboratory]: translate('laboratory'),
        [OrganizationType.Contractor]: translate('contractor'),
    };

    return getIfExist(StatusDic, value);
}

export function getConditionsAgreementTranslation(value: ConditionsAgreement): string {
    const StatusDic: AllKeyOf<ConditionsAgreement> = {
        [ConditionsAgreement.Without]: translate('withoutApproval'),
        [ConditionsAgreement.Required]: translate('coordinationRequired'),
        [ConditionsAgreement.Excluded]: translate('notIncludedInInsurance'),
    };

    return getIfExist(StatusDic, value);
}

export function getGrantSourceTranslation(value: GrantSource): string {
    const StatusDic: AllKeyOf<GrantSource> = {
        [GrantSource.Reservation]: translate('reservation'),
        [GrantSource.Referral]: translate('referral'),
        [GrantSource.Episode]: translate('episode'),
        [GrantSource.Entry]: translate('entryRecord'),
        [GrantSource.Task]: translate('task'),
        [GrantSource.Recommendation]: translate('recommendation'),
        [GrantSource.RecurringSubscription]: translate('subscription'),
        [GrantSource.Self]: translate('ownEhr'),
    };

    return getIfExist(StatusDic, value);
}
